<template>
    <div class="member-review-page">
        <div class="container-fluid">
            <div class="row">
                <div class="col-12">
                    <div class="page-title">
                        <h1>Review Your Enrollment</h1>
                    </div>
                </div>
                <div class="col-12">
                    <b-skeleton-table v-if="isLoading" :rows="8" :columns="1"></b-skeleton-table>
                    <div v-else-if="loadError" class="review-thanks text-center">
                        <h2>We couldn't load your enrollment</h2>
                        <p>Please check your connection and try again.</p>
                        <button type="button" class="submitSignature" @click="load">Try again</button>
                    </div>
                    <div v-else-if="submitted" class="review-thanks text-center">
                        <h2>Thank you</h2>
                        <p>Your review has been submitted. You can close this page.</p>
                    </div>
                    <div v-else ref="tabsCard" class="page-card enrollmentEdit-card">
                        <b-card no-body>
                            <b-tabs card v-model="tabIndex">
                                <b-tab title="Information Details" active>
                                    <b-card-text>
                                        <plan-summary />
                                        <info-details @saved="nextTab" @next="nextTab" />
                                    </b-card-text>
                                </b-tab>
                                <b-tab title="Contact Details">
                                    <b-card-text>
                                        <contact-info @saved="nextTab" @next="nextTab" />
                                    </b-card-text>
                                </b-tab>
                                <b-tab v-for="(tab, tabPosition) in visibleTabs" :key="tab.tab_name" :title="getTabName(tab.tab_name)" :disabled="tabPosition > pregnancyBlockedAfter">
                                    <b-card-text>
                                        <component
                                            :is="tabComponents[tab.tab_name]"
                                            @saved="nextTab"
                                            @next="nextTab"
                                            @pregnancy-restricted="setPregnancyRestricted(tab.tab_name, $event)"
                                        />
                                    </b-card-text>
                                </b-tab>
                                <b-tab title="Billing Information" :disabled="isPregnancyRestricted">
                                    <b-card-text>
                                        <billing-info @saved="nextTab" @next="nextTab" />
                                    </b-card-text>
                                </b-tab>
                                <b-tab title="Agreement" v-if="agreements.length" :disabled="isPregnancyRestricted">
                                    <b-card-text>
                                        <agreement-info :texts="agreements" @next="nextTab" />
                                    </b-card-text>
                                </b-tab>
                                <b-tab title="Review &amp; Sign" :disabled="isPregnancyRestricted">
                                    <b-card-text>
                                        <review-and-sign @submitted="submitted = true" />
                                    </b-card-text>
                                </b-tab>
                            </b-tabs>
                        </b-card>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import '@/assets/css/member-review.css';
import '@/validation/memberReviewRules';
import reviewApi from '@/resource/memberReviewApi';
import { notifyError } from '@/resource/memberReviewNotify';
import InfoDetails from '@/components/MemberReview/InfoDetails.vue';
import PlanSummary from '@/components/MemberReview/PlanSummary.vue';
import ContactInfo from '@/components/MemberReview/ContactInfo.vue';
import EmployerInfo from '@/components/MemberReview/EmployerInfo.vue';
import BeneficiaryInfo from '@/components/MemberReview/BeneficiaryInfo.vue';
import PersonalQuestionaire from '@/components/MemberReview/PersonalQuestionaire.vue';
import DependentInfo from '@/components/MemberReview/DependentInfo.vue';
import BillingInfo from '@/components/MemberReview/BillingInfo.vue';
import ReviewAndSign from '@/components/MemberReview/ReviewAndSign.vue';
import AgreementInfo from '@/components/MemberReview/AgreementInfo.vue';

// Backend tab_name -> component. Unknown tab names are ignored.
const TAB_COMPONENTS = {
    employer_info: EmployerInfo,
    beneficiary_info: BeneficiaryInfo,
    primary_health_info: PersonalQuestionaire,
    dependent_info: DependentInfo,
};

export default {
    name: 'MemberReview',
    components: {
        InfoDetails,
        PlanSummary,
        ContactInfo,
        BillingInfo,
        ReviewAndSign,
        AgreementInfo,
    },
    data: () => ({
        tabs: [],
        isLoading: true,
        loadError: false,
        submitted: false,
        tabIndex: 0,
        agreements: [],
        tabComponents: TAB_COMPONENTS,
        pregnancyRestrictedTabs: {},
    }),
    computed: {
        visibleTabs() {
            return this.tabs.filter((tab) => this.tabComponents[tab.tab_name]);
        },
        // Position in visibleTabs of the first tab with a "Yes" to the pregnancy question;
        // no later tab can be opened while it stays Yes.
        pregnancyBlockedAfter() {
            const position = this.visibleTabs.findIndex((tab) => this.pregnancyRestrictedTabs[tab.tab_name]);
            return position === -1 ? Infinity : position;
        },
        isPregnancyRestricted() {
            return this.pregnancyBlockedAfter !== Infinity;
        },
    },
    methods: {
        setPregnancyRestricted(tabName, restricted) {
            this.$set(this.pregnancyRestrictedTabs, tabName, restricted);
        },
        // After a tab saves, go to the next one (ending on Review & Sign) and bring it into view.
        nextTab() {
            this.tabIndex += 1;
            this.$nextTick(() => {
                const card = this.$refs.tabsCard;
                if (card && card.scrollIntoView) card.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
        },
        async load() {
            this.isLoading = true;
            this.loadError = false;
            try {
                const status = await reviewApi.getStatus();
                this.submitted = !!(status.data.data && status.data.data.submitted);
                if (!this.submitted) {
                    const [tabsRes, agreementsRes] = await Promise.all([reviewApi.getTabs(), reviewApi.getAgreements()]);
                    this.tabs = tabsRes.data.data || [];
                    this.agreements = agreementsRes.data.data || [];
                }
            } catch (err) {
                // Never show a partial set of tabs: the member could sign without seeing every section.
                this.loadError = true;
                notifyError(this, err);
            } finally {
                this.isLoading = false;
            }
        },
        getTabName(tab) {
            return tab.replace(/_/g, ' ').replace(/(^\w{1})|(\s+\w{1})/g, letter => letter.toUpperCase());
        },
    },
    mounted() {
        this.load();
    },
};
</script>
